import { useEffect, useRef, useCallback } from 'react';
import { StationCard } from './StationCard';
import { Fuel, SearchX, Loader2 } from 'lucide-react';

export const StationList = ({ 
  stations, 
  selectedFuels, 
  sortBy, 
  hasMore, 
  isLoadingMore, 
  onLoadMore,
  totalStations 
}) => {
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasMore && !isLoadingMore) {
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '100px',
      threshold: 0
    };
    
    observerRef.current = new IntersectionObserver(handleObserver, option);
    
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  if (!stations || stations.length === 0) {
    return (
      <div 
        data-testid="station-list-empty"
        className="flex flex-col items-center justify-center py-16 px-4 text-center"
      >
        <SearchX className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
        <h3 className="font-heading text-xl font-bold uppercase text-gray-600 dark:text-gray-400 mb-2">
          Aucune station trouvée
        </h3>
        <p className="text-gray-500 dark:text-gray-500 text-sm max-w-xs">
          Essayez une autre recherche ou modifiez vos filtres de carburant.
        </p>
      </div>
    );
  }

  // Sort stations
  const sortedStations = [...stations].sort((a, b) => {
    switch (sortBy) {
      case 'price':
        // Get lowest price from each station
        const getLowestPrice = (station) => {
          const fuels = selectedFuels.length > 0 
            ? station.fuels?.filter(f => selectedFuels.includes(f.id))
            : station.fuels;
          const prices = fuels?.filter(f => f.price && !f.rupture).map(f => f.price) || [];
          return prices.length > 0 ? Math.min(...prices) : Infinity;
        };
        return getLowestPrice(a) - getLowestPrice(b);
      case 'distance':
        return (a.distance || Infinity) - (b.distance || Infinity);
      case 'name':
        const nameA = a.brand?.name || a.brand?.short_name || 'ZZZZ';
        const nameB = b.brand?.name || b.brand?.short_name || 'ZZZZ';
        return nameA.localeCompare(nameB);
      default:
        return 0;
    }
  });

  // Find best price station
  const getBestPriceStationId = () => {
    let bestId = null;
    let bestPrice = Infinity;
    
    for (const station of sortedStations) {
      const fuels = selectedFuels.length > 0 
        ? station.fuels?.filter(f => selectedFuels.includes(f.id))
        : station.fuels;
      const prices = fuels?.filter(f => f.price && !f.rupture).map(f => f.price) || [];
      const minPrice = prices.length > 0 ? Math.min(...prices) : Infinity;
      
      if (minPrice < bestPrice) {
        bestPrice = minPrice;
        bestId = station.id;
      }
    }
    
    return bestId;
  };

  const bestPriceId = getBestPriceStationId();

  return (
    <div data-testid="station-list" className="flex flex-col gap-4 px-4 py-4">
      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
        <Fuel className="w-4 h-4" />
        <span className="text-sm font-bold uppercase tracking-wider">
          {stations.length}{totalStations > stations.length ? ` / ${totalStations}` : ''} station{stations.length > 1 ? 's' : ''} 
        </span>
      </div>
      
      {sortedStations.map((station) => (
        <StationCard 
          key={station.id} 
          station={station} 
          isBestPrice={station.id === bestPriceId}
          selectedFuels={selectedFuels}
        />
      ))}
      
      {/* Load more trigger */}
      <div ref={loadMoreRef} className="py-4">
        {isLoadingMore && (
          <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-5 h-5 spinner" />
            <span className="text-sm font-bold uppercase tracking-wider">Chargement...</span>
          </div>
        )}
        {hasMore && !isLoadingMore && (
          <button
            onClick={onLoadMore}
            data-testid="load-more-button"
            className="w-full p-3 bg-white dark:bg-gray-800 border-2 border-black dark:border-gray-600 text-black dark:text-white font-bold uppercase tracking-wider hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Charger plus de stations
          </button>
        )}
        {!hasMore && stations.length > 0 && (
          <p className="text-center text-gray-500 dark:text-gray-400 text-sm">
            Toutes les stations ont été chargées
          </p>
        )}
      </div>
    </div>
  );
};
